/**
 * The intake flow's graph, and every edit the builder makes to it (ACL-338).
 *
 * One `Graph` is what the canvas draws and what the server stores, one for one. Every edit is a
 * pure `Graph -> Graph` helper, so the page can push each through the history hook and one undo
 * takes back a whole delete whatever it removed.
 *
 * **A branch with no conditions is a catch-all, and it is optional.** When a step has one it is
 * last, and it takes every lead the branches above did not name. A step without one asks only
 * what the admin listed: a lead matching none of its branches stops there, which the validator
 * remarks on and the rep's panel shows. Listing exactly the firms, campaigns or options you take
 * is a real thing to mean, so the builder never adds one on its own.
 *
 * **Deleting never cascades silently.** A step with one branch out splices, so the chain keeps
 * going; a split offers two choices and says what each costs; an end card is never deleted alone. Nothing here touches a question's definition or a lead's
 * answer: both live on the contact form, and the builder only ever rearranges which are asked.
 */

export type NodeKind = "question" | "paths" | "outcome";
export type OutcomeKind = "continue" | "flag" | "reject";

export interface FlowNode {
  key: string;
  kind: NodeKind;
  title: string;
  field_key: string | null;
  outcome: OutcomeKind | null;
  config: Record<string, unknown>;
}

export interface FlowEdge {
  from_key: string;
  to_key: string;
  position: number;
  label: string | null;
  conditions: Clause[];
}

export interface Clause {
  /** Where the fact comes from. A campaign is firm, state and language together (ADR-045), so
   *  it is the coarsest scope; the accident's state is an ordinary question, not a source. */
  source: "answer" | "firm" | "campaign" | "language";
  field?: string;
  op: string;
  value?: unknown;
  /**
   * How this condition joins the one before it. `and` is the default and is never stored.
   *
   * `and` binds tighter than `or`, as it does everywhere else people write rules, so
   * `A or B and C` means `A or (B and C)`. The first condition's own join is ignored.
   */
  join?: "and" | "or";
}

export interface Graph {
  start: string | null;
  state_field_key: string | null;
  nodes: FlowNode[];
  edges: FlowEdge[];
}

export interface Problem {
  code: string;
  message: string;
  key: string | null;
}

export interface Report {
  errors: Problem[];
  warnings: Problem[];
}

/** A question the builder may attach: a field of the contact form that holds a value. */
export interface Question {
  key: string;
  title: string;
  type: string;
  section: string;
  options: { key: string; label: string }[];
  can_branch: boolean;
}

export const NODES_MAX = 300;
export const EDGES_MAX = 20;

/** The steps a flow can hold, as the plus menu offers them. */
export const STEP_KINDS = [
  { kind: "question", label: "Question", hint: "Ask one of the contact form's questions" },
  { kind: "paths", label: "Paths", hint: "Send people different ways on what they answered" },
  { kind: "flag", label: "Flag for review", hint: "Raise it with a supervisor and carry on" },
  { kind: "reject", label: "Not qualified", hint: "Propose an outcome for the rep to record" },
  { kind: "continue", label: "Qualified", hint: "End the intake" },
] as const;

export function emptyGraph(): Graph {
  return { start: null, state_field_key: null, nodes: [], edges: [] };
}

export function nodeOf(graph: Graph, key: string | null): FlowNode | undefined {
  return key ? graph.nodes.find((n) => n.key === key) : undefined;
}

/** The branches out of a step, in the order the engine tries them. */
export function outOf(graph: Graph, key: string): FlowEdge[] {
  return graph.edges.filter((e) => e.from_key === key).sort((a, b) => a.position - b.position);
}

export function intoOf(graph: Graph, key: string): FlowEdge[] {
  return graph.edges.filter((e) => e.to_key === key);
}

export function isFallback(edge: FlowEdge): boolean {
  return edge.conditions.length === 0;
}

export function endsTheWalk(node: FlowNode): boolean {
  return node.kind === "outcome" && (node.outcome === "continue" || node.outcome === "reject");
}

/**
 * What a split already varies by, read back off its own branches (ACL-340).
 *
 * The Vary by dialog opens on this, so reopening it shows the firms or campaigns that are there
 * rather than an empty list an admin has to retype from memory. Null when the step has no
 * branches keyed on a firm or a campaign — a question branching on its own answer, or a split an
 * admin wrote conditions for by hand.
 *
 * The ids come back as strings whatever they are stored as: a firm's is a number and a
 * campaign's is a uuid, and the dialog ticks a list of strings.
 */
export function varyBySoFar(
  graph: Graph,
  key: string,
): { source: "firm" | "campaign"; ids: string[] } | null {
  const ids: string[] = [];
  let source: "firm" | "campaign" | null = null;
  for (const edge of outOf(graph, key)) {
    for (const clause of edge.conditions) {
      if (clause.source !== "firm" && clause.source !== "campaign") continue;
      // A step varies by one thing: the first source found decides, and a stray clause of the
      // other kind is left alone rather than mixed into the pick.
      source ??= clause.source;
      if (clause.source !== source) continue;
      const value = clause.value;
      for (const one of Array.isArray(value) ? value : [value]) {
        if (one === null || one === undefined) continue;
        const asText = String(one);
        if (!ids.includes(asText)) ids.push(asText);
      }
    }
  }
  return source && ids.length ? { source, ids } : null;
}

/** Whether a step fans out: a Paths step, or a question branching on its own answer. */
export function isSplit(graph: Graph, key: string): boolean {
  return outOf(graph, key).length > 1;
}

/** Where a split's branches rejoin, which the split itself remembers. */
export function mergeOf(graph: Graph, key: string): string | null {
  const node = nodeOf(graph, key);
  const merge = node?.config?.merge;
  return typeof merge === "string" ? merge : null;
}

/**
 * Where a split's branches come back together, worked out from the edges themselves.
 *
 * The stored `config.merge` is a cache and it goes stale: `removeNode` writes the deleted step's
 * own onward key into it, which is null when that step was the last one. A null merge makes every
 * column walk the whole tail of the flow, so the steps after the split are drawn once per branch.
 * This is the fallback that keeps that from showing.
 *
 * It is the nearest common descendant: breadth-first from each branch, never stepping back
 * through the split, then the first key in the first branch's order that every other branch also
 * reaches. Null when the branches never meet, which is a flow that ends differently per path.
 */
export function postDominator(graph: Graph, key: string): string | null {
  const outs = outOf(graph, key);
  if (outs.length < 2) return null;
  const budget = graph.nodes.length + 1;
  const orders: string[][] = [];
  const hits: Set<string>[] = [];
  for (const edge of outs) {
    const seen = new Set<string>();
    const order: string[] = [];
    const queue = [edge.to_key];
    let spent = 0;
    while (queue.length && spent < budget) {
      const at = queue.shift()!;
      if (at === key || seen.has(at) || !nodeOf(graph, at)) continue;
      seen.add(at);
      order.push(at);
      spent += 1;
      for (const next of outOf(graph, at)) queue.push(next.to_key);
    }
    orders.push(order);
    hits.push(seen);
  }
  const [first, ...rest] = orders;
  for (const candidate of first ?? []) {
    if (rest.every((_, n) => hits[n + 1]!.has(candidate))) return candidate;
  }
  return null;
}

/**
 * Why "make a path per answer" would do nothing here, or null when it would work.
 *
 * The drawer and the canvas both offer the action, and `branchOnAnswer` has its own guards; with
 * no shared predicate the two drifted apart and the button went quiet instead of saying why.
 */
export function branchOnAnswerBlocked(
  graph: Graph,
  key: string,
  question?: Question | null,
): string | null {
  const node = nodeOf(graph, key);
  if (!node) return "That step is no longer on the flow.";
  if (node.kind !== "question") return "Only a question can branch on its answer.";
  if (!question) return "Pick the question this step asks first.";
  if (!question.can_branch) {
    return "Answers to this question are free text, so there is nothing to make a path per.";
  }
  if (outOf(graph, key).length > 1) return "This question already has paths.";
  return null;
}

/** Every step the walk can arrive at, following every branch from the start. */
export function reachable(graph: Graph): Set<string> {
  const seen = new Set<string>();
  if (!graph.start || !nodeOf(graph, graph.start)) return seen;
  const pending = [graph.start];
  while (pending.length) {
    const key = pending.pop()!;
    if (seen.has(key)) continue;
    seen.add(key);
    for (const edge of outOf(graph, key)) if (nodeOf(graph, edge.to_key)) pending.push(edge.to_key);
  }
  return seen;
}

/**
 * The steps that would be left with nothing leading to them once `going` are gone.
 *
 * This is what a delete dialog counts: not the whole subtree, but what genuinely becomes
 * unreachable, so a step another branch still reaches is never counted as lost.
 */
export function orphansWithout(graph: Graph, going: Set<string>): string[] {
  const kept: Graph = {
    ...graph,
    nodes: graph.nodes.filter((n) => !going.has(n.key)),
    edges: graph.edges.filter((e) => !going.has(e.from_key) && !going.has(e.to_key)),
  };
  const live = reachable(kept);
  return kept.nodes.filter((n) => !live.has(n.key)).map((n) => n.key);
}

/**
 * The field keys every path to this step has already asked.
 *
 * What a branch out of it may read: the engine only counts answers it met on the way, so a
 * condition on a question asked later, or on another branch, can never be true.
 */
export function askedBefore(graph: Graph, key: string): string[] {
  const known = new Map<string, Set<string>>();
  if (!graph.start || !nodeOf(graph, graph.start)) return [];
  const pending: { at: string; carried: Set<string> }[] = [{ at: graph.start, carried: new Set() }];
  let guard = 0;
  const limit = Math.max(graph.nodes.length * EDGES_MAX * 4, 64);
  while (pending.length && guard < limit) {
    guard += 1;
    const { at, carried } = pending.pop()!;
    const node = nodeOf(graph, at);
    if (!node) continue;
    const had = known.get(at);
    // Every path has to have asked it, so paths meeting here keep only what they share.
    const merged = had ? new Set([...had].filter((k) => carried.has(k))) : new Set(carried);
    if (had && merged.size === had.size) continue;
    known.set(at, merged);
    const onward = new Set(merged);
    if (node.kind === "question" && node.field_key) onward.add(node.field_key);
    for (const edge of outOf(graph, at)) pending.push({ at: edge.to_key, carried: onward });
  }
  const here = new Set(known.get(key) ?? []);
  // A question's own branches read its own answer: the walk records it before it chooses.
  const node = nodeOf(graph, key);
  if (node?.kind === "question" && node.field_key) here.add(node.field_key);
  return [...here];
}

/** A key nothing else is using, from a title. */
export function keyFrom(title: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  const base =
    title
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/^([0-9])/, "s$1")
      .slice(0, 36) || "step";
  if (!used.has(base)) return base;
  for (let n = 2; n < 1000; n += 1) {
    const candidate = `${base}_${n}`.slice(0, 40);
    if (!used.has(candidate)) return candidate;
  }
  return `${base}_${used.size}`.slice(0, 40);
}

/**
 * Give every branch of a fresh split its own copy of what followed the step.
 *
 * A split is made where one step already led to another, so without this every branch points at
 * that same step: the board draws the branches as empty lanes and the step once underneath them,
 * which reads as a stray card hanging outside the split rather than as the end of each path.
 * Each branch gets its own, so an admin can take one path somewhere different without the change
 * following every other path.
 *
 * Only the chain is copied, and only as far as it stays single-file: a step that already fans
 * out, or one that several paths reach, is left shared — copying those would duplicate whole
 * sub-trees nobody asked for.
 */
function copyTailPerBranch(graph: Graph, key: string, tail: string): Graph {
  const mine = outOf(graph, key);
  if (mine.length < 2) return graph;

  // The single-file run after the split, which is what each branch gets a copy of.
  const chain: string[] = [];
  let at: string | null = tail;
  while (at && nodeOf(graph, at) && !chain.includes(at)) {
    // Something else leads here too, so it is not this split's to copy.
    if (intoOf(graph, at).some((e) => e.from_key !== key && !chain.includes(e.from_key))) break;
    chain.push(at);
    const next = outOf(graph, at);
    if (next.length !== 1) break;
    at = next[0]!.to_key;
  }
  if (!chain.length) return graph;

  // The branches no longer rejoin, so the split remembers no merge. Left behind, the stored one
  // makes the first branch's own copy draw below the split as a shared tail — the stray card.
  let next: Graph = {
    ...graph,
    nodes: graph.nodes.map((n) => {
      if (n.key !== key) return n;
      const { merge: _gone, ...rest } = n.config ?? {};
      return { ...n, config: rest };
    }),
  };
  const edges = [...graph.edges];
  // The first branch keeps the original; the rest get copies, so nothing is orphaned.
  for (const branch of mine.slice(1)) {
    const made = new Map<string, string>();
    for (const one of chain) {
      const source = nodeOf(next, one)!;
      const key_ = keyFrom(source.title, next.nodes.map((n) => n.key));
      made.set(one, key_);
      next = { ...next, nodes: [...next.nodes, { ...source, key: key_, config: { ...source.config } }] };
    }
    for (const one of chain) {
      for (const edge of outOf(graph, one)) {
        const to = made.get(edge.to_key) ?? edge.to_key;
        edges.push({ ...edge, from_key: made.get(one)!, to_key: to });
      }
    }
    const at_ = edges.findIndex(
      (e) => e.from_key === key && e.position === branch.position,
    );
    if (at_ >= 0) edges[at_] = { ...edges[at_]!, to_key: made.get(chain[0]!)! };
  }
  return { ...next, edges };
}

function withEdges(graph: Graph, edges: FlowEdge[]): Graph {
  return { ...graph, edges };
}

/** Renumber a step's branches so their positions stay 0, 1, 2 with the Fallback last. */
function tidy(graph: Graph, key: string): Graph {
  const mine = outOf(graph, key);
  const conditioned = mine.filter((e) => !isFallback(e));
  const fallbacks = mine.filter(isFallback);
  const ordered = [...conditioned, ...fallbacks];
  const rest = graph.edges.filter((e) => e.from_key !== key);
  return withEdges(graph, [...rest, ...ordered.map((e, i) => ({ ...e, position: i }))]);
}

// --- adding -------------------------------------------------------------------------------------

/**
 * Put a new step on the branch between two cards: the branch now ends at it, and it carries on
 * to where the branch used to go. A chain therefore never breaks when something is added.
 */
export function addAfter(graph: Graph, edge: FlowEdge, node: FlowNode): Graph {
  const nodes = [...graph.nodes, node];
  const edges = graph.edges.map((e) =>
    e.from_key === edge.from_key && e.position === edge.position ? { ...e, to_key: node.key } : e,
  );
  if (!endsTheWalk(node)) {
    edges.push({
      from_key: node.key,
      to_key: edge.to_key,
      position: 0,
      label: null,
      conditions: [],
    });
  }
  return tidy({ ...graph, nodes, edges }, node.key);
}

/** The first step of the flow, when it had none, or a new one before the current first. */
export function addFirst(graph: Graph, node: FlowNode): Graph {
  const nodes = [...graph.nodes, node];
  const edges = [...graph.edges];
  if (graph.start && !endsTheWalk(node)) {
    edges.push({
      from_key: node.key,
      to_key: graph.start,
      position: 0,
      label: null,
      conditions: [],
    });
  }
  return { ...graph, start: node.key, nodes, edges };
}

/**
 * A step straight after another, whether or not that one already leads somewhere.
 *
 * `addAfter` needs an edge to splice into, and the last step of a chain has none — the usual
 * state while a flow is being built up one question at a time. The page used to fall back to
 * `addFirst` there, which put each new question at the *front*: ask three in a row and they came
 * out in reverse.
 */
export function addAfterNode(graph: Graph, key: string, node: FlowNode): Graph {
  // A split means every branch, not the first one: `outOf(key)[0]` put the new step on the
  // leftmost path alone and left the others going where they always went.
  if (outOf(graph, key).length > 1) return addAfterEveryBranch(graph, key, node);
  const onward = outOf(graph, key)[0];
  if (onward) return addAfter(graph, onward, node);
  if (!nodeOf(graph, key)) return graph;
  return {
    ...graph,
    nodes: [...graph.nodes, node],
    edges: [
      ...graph.edges,
      { from_key: key, to_key: node.key, position: 0, label: null, conditions: [] },
    ],
  };
}

/**
 * Where every branch out of a step leads to the same place, that place. Null when they part ways.
 *
 * This is read from the edges themselves rather than from `mergeOf` or `postDominator`: those
 * name where the branches eventually reconverge, which can be further down the flow than the
 * branches' own targets, and inserting there is a different edit from the one being asked for.
 */
export function sharedLanding(graph: Graph, key: string): string | null {
  const mine = outOf(graph, key);
  if (mine.length < 2) return null;
  const landings = new Set(mine.map((e) => e.to_key));
  return landings.size === 1 ? (mine[0]?.to_key ?? null) : null;
}

/**
 * Why one step cannot follow every path out of this one, or null when it can.
 *
 * Only a split whose paths already land on one card has an answer. A single step after paths
 * that end in different places would need more than one way onward with nothing to tell them
 * apart, and the engine takes the first branch that matches (`walk._next_edge`), so one of the
 * paths would quietly stop being walked.
 */
export function addAfterEveryBranchBlocked(graph: Graph, key: string): string | null {
  if (!nodeOf(graph, key)) return "That step is no longer on the flow.";
  if (outOf(graph, key).length < 2) return "This step has only one way on.";
  if (!sharedLanding(graph, key)) {
    return (
      "These paths end in different places, so one step cannot follow them all." +
      " Use a path's own menu to add it to that path."
    );
  }
  return null;
}

/**
 * Why a step cannot go after this one, or null when it can.
 *
 * A step with one way on always has an answer. A split only has one where its branches land on
 * the same card, since the new step has a single way onward — so the page says which, rather
 * than opening a dialog whose every choice would quietly do nothing.
 */
export function addAfterNodeBlocked(graph: Graph, key: string): string | null {
  if (!nodeOf(graph, key)) return "That step is no longer on the flow.";
  if (outOf(graph, key).length > 1) return addAfterEveryBranchBlocked(graph, key);
  return null;
}

/**
 * A step after a split, on every branch at once.
 *
 * One shared step, not a copy per branch: a step has one way onward, so the branches rejoin on it
 * and carry on to where they all went before. Each branch keeps its own label and conditions —
 * those say who takes the path, which the inserted step does not change; only where the path
 * lands moves.
 */
export function addAfterEveryBranch(graph: Graph, key: string, node: FlowNode): Graph {
  const landing = sharedLanding(graph, key);
  if (!landing || addAfterEveryBranchBlocked(graph, key)) return graph;
  const edges = graph.edges.map((e) => (e.from_key === key ? { ...e, to_key: node.key } : e));
  // An ending has no way on, so putting one here would strand the card the branches used to
  // reach along with everything after it.
  if (endsTheWalk(node)) return graph;
  edges.push({ from_key: node.key, to_key: landing, position: 0, label: null, conditions: [] });
  return tidy({ ...graph, nodes: [...graph.nodes, node], edges }, node.key);
}

/**
 * Why a step cannot go before this one, or null when it can.
 *
 * "Before" means on the ways in, so a card nothing arrives at has no before to speak of — unless
 * it is the first step of the flow, where the new step becomes the first instead.
 */
export function addBeforeBlocked(graph: Graph, key: string): string | null {
  if (!nodeOf(graph, key)) return "That step is no longer on the flow.";
  if (graph.nodes.length >= NODES_MAX) return "This flow has as many steps as it can hold.";
  const arrivals = graph.edges.filter((e) => e.to_key === key && e.from_key !== key);
  if (arrivals.length === 0 && graph.start !== key) {
    return "Nothing leads to this step yet, so there is no path to put one on.";
  }
  return null;
}

/**
 * A step before another: everything that led to that card now leads to the new one, and the new
 * one carries on to it.
 *
 * **Shared, never copied.** An end card that three branches reach keeps being one card; each
 * branch's label and conditions ride on its own edge, so re-pointing where a branch lands changes
 * nothing about who takes it. This is what lets a question go *above* a Qualified card that every
 * path shares, which naming an edge could not express.
 *
 * A self-edge is left alone: a step deliberately looping back to itself should keep doing that
 * rather than be quietly rerouted through the new one.
 */
export function addBefore(graph: Graph, key: string, node: FlowNode): Graph {
  if (addBeforeBlocked(graph, key)) return graph;
  // An ending has no way on, so the card it was put before — and everything after it — would be
  // stranded with nothing leading to it.
  if (endsTheWalk(node)) return graph;
  const edges = graph.edges.map((e) =>
    e.to_key === key && e.from_key !== key ? { ...e, to_key: node.key } : e,
  );
  edges.push({ from_key: node.key, to_key: key, position: 0, label: null, conditions: [] });
  // A split's cached merge is a key, and the step it names has just moved down one.
  const nodes = [...graph.nodes, node].map((n) =>
    n.config?.merge === key ? { ...n, config: { ...n.config, merge: node.key } } : n,
  );
  const start = graph.start === key ? node.key : graph.start;
  return tidy({ ...graph, start, nodes, edges }, node.key);
}

/** A branch out of a split, inserted before the Fallback so that stays last. */
export function addBranch(graph: Graph, key: string, to: string, conditions: Clause[] = []): Graph {
  const mine = outOf(graph, key);
  if (mine.length >= EDGES_MAX) return graph;
  const edge: FlowEdge = { from_key: key, to_key: to, position: 0, label: null, conditions };
  return tidy(withEdges(graph, [...graph.edges, edge]), key);
}

/**
 * A branch out of a step, with somewhere for it to land made if there is nowhere yet.
 *
 * A step the admin has just put down leads nowhere, so there is no target to branch to and no
 * stored merge to borrow one from. The page used to give up quietly there — "Add a branch" did
 * nothing on a fresh step and only started working once a step had been added after it.
 */
export function addBranchHere(graph: Graph, key: string, conditions: Clause[] = []): Graph {
  if (!nodeOf(graph, key)) return graph;
  const mine = outOf(graph, key);
  if (mine.length >= EDGES_MAX) return graph;

  // Where the branches rejoin, when they still do. A split whose branches each carry their own
  // copy of what followed has no merge, and a new branch gets its own end too: landing it on a
  // sibling's card would make an edit to one path quietly change another.
  const landing = mergeOf(graph, key);
  if (landing && nodeOf(graph, landing)) return addBranch(graph, key, landing, conditions);

  const made = keyFrom("qualified", graph.nodes.map((n) => n.key));
  const withEnd: Graph = {
    ...graph,
    nodes: [
      ...graph.nodes,
      {
        key: made,
        kind: "outcome",
        title: "Qualified",
        field_key: null,
        outcome: "continue",
        config: {},
      },
    ],
  };
  return addBranch(withEnd, key, made, conditions);
}

/** Turn a question into a split: one branch per option of its answer, and a Fallback. */
export function branchOnAnswer(graph: Graph, key: string, question: Question): Graph {
  const node = nodeOf(graph, key);
  if (!node || node.kind !== "question" || !question.can_branch) return graph;
  const mine = outOf(graph, key);
  // Already a split: branching again would throw away the paths that are there.
  if (mine.length > 1) return graph;

  // A question with nothing after it — the usual state once the step that followed it has been
  // deleted — still branches. Its paths need somewhere to land, so an end is made for them to
  // share and the admin builds each path out from there. Refusing here instead is what made
  // "make a path per answer" look like a button that does nothing.
  let next = graph;
  let onward: string;
  if (mine.length === 1) {
    onward = mine[0]!.to_key;
  } else {
    onward = keyFrom("qualified", next.nodes.map((n) => n.key));
    next = {
      ...next,
      nodes: [
        ...next.nodes,
        {
          key: onward,
          kind: "outcome",
          title: "Qualified",
          field_key: null,
          outcome: "continue",
          config: {},
        },
      ],
    };
  }
  graph = next;
  const options = question.type === "boolean"
    ? [{ key: "true", label: "Yes" }, { key: "false", label: "No" }]
    : question.options;
  const branches: FlowEdge[] = options.slice(0, EDGES_MAX - 1).map((option, index) => ({
    from_key: key,
    to_key: onward,
    position: index,
    label: option.label,
    conditions: [
      {
        source: "answer" as const,
        field: question.key,
        op: "eq",
        value: question.type === "boolean" ? option.key === "true" : option.key,
      },
    ],
  }));
  const rest = graph.edges.filter((e) => e.from_key !== key);
  // No catch-all is added. The options an admin defined are what they meant, and a lead whose
  // answer is none of them stops there — which the validator remarks on and the rep's panel
  // shows. Forcing an "Everyone else" path put one on every flow whether or not it wanted one.
  const nodes = graph.nodes.map((n) =>
    n.key === key ? { ...n, config: { ...n.config, merge: onward } } : n,
  );
  // Each branch gets its own copy of whatever followed, so the step that was there does not sit
  // under the split looking like a card nobody attached.
  return copyTailPerBranch({ ...graph, nodes, edges: [...rest, ...branches] }, key, onward);
}

/**
 * Make one path per firm or per campaign, with the base flow as the last path.
 *
 * The same shape as branching on an answer, but keyed on who the lead belongs to rather than on
 * what they said: a firm's own questions, or a campaign's. A campaign is firm, state and language
 * together, so it is the coarsest of the three; the state a rule means is the accident's, which
 * is an ordinary question and branches like any other answer.
 */
export function varyBy(
  graph: Graph,
  key: string,
  source: "firm" | "campaign",
  picked: { id: string | number; label: string }[],
): Graph {
  const mine = outOf(graph, key);
  if (!nodeOf(graph, key) || picked.length === 0) return graph;

  // A Paths step taken from the plus menu leads nowhere yet, so there was no onward key to hang
  // the branches on and this returned the graph untouched: picking three firms made no paths at
  // all until a step had been added after it. An end is made for them to share, as
  // `addBranchHere` does.
  //
  // A step that already fans out is re-varied rather than refused. "Pick other firms or
  // campaigns" is offered on exactly those steps, and refusing here made it a button that closed
  // its dialog and did nothing: the admin picked three firms and the canvas did not move. The
  // branches below are rebuilt from the new pick, hanging on whatever the existing ones already
  // shared, so re-varying keeps the rest of the flow where it was.
  let base = graph;
  let onward: string;
  if (mine.length >= 1) {
    onward = mergeOf(graph, key) ?? mine[0]!.to_key;
  } else {
    onward = keyFrom("qualified", base.nodes.map((n) => n.key));
    base = {
      ...base,
      nodes: [
        ...base.nodes,
        {
          key: onward,
          kind: "outcome",
          title: "Qualified",
          field_key: null,
          outcome: "continue",
          config: {},
        },
      ],
    };
  }
  graph = base;
  const branches: FlowEdge[] = picked.slice(0, EDGES_MAX - 1).map((one, index) => ({
    from_key: key,
    to_key: onward,
    position: index,
    label: one.label,
    conditions: [{ source, op: "eq", value: one.id }],
  }));
  // The firms or campaigns picked are the ones this step varies by; a lead on another takes no
  // path here unless an admin adds a catch-all themselves.
  const nodes = graph.nodes.map((n) =>
    n.key === key ? { ...n, config: { ...n.config, merge: onward } } : n,
  );
  const rest = graph.edges.filter((e) => e.from_key !== key);
  return copyTailPerBranch({ ...graph, nodes, edges: [...rest, ...branches] }, key, onward);
}

// --- changing -----------------------------------------------------------------------------------

export function updateNode(graph: Graph, key: string, patch: Partial<FlowNode>): Graph {
  return {
    ...graph,
    nodes: graph.nodes.map((n) => (n.key === key ? { ...n, ...patch } : n)),
  };
}

export function updateEdge(
  graph: Graph,
  from: string,
  position: number,
  patch: Partial<FlowEdge>,
): Graph {
  return {
    ...graph,
    edges: graph.edges.map((e) =>
      e.from_key === from && e.position === position ? { ...e, ...patch } : e,
    ),
  };
}

/** Move a branch one place along. The Fallback stays last whatever happens. */
export function moveBranch(graph: Graph, key: string, position: number, by: -1 | 1): Graph {
  const mine = outOf(graph, key).filter((e) => !isFallback(e));
  const at = mine.findIndex((e) => e.position === position);
  const to = at + by;
  if (at < 0 || to < 0 || to >= mine.length) return graph;
  const reordered = [...mine];
  const [moved] = reordered.splice(at, 1);
  reordered.splice(to, 0, moved!);
  const fallbacks = outOf(graph, key).filter(isFallback);
  const rest = graph.edges.filter((e) => e.from_key !== key);
  return withEdges(graph, [
    ...rest,
    ...[...reordered, ...fallbacks].map((e, i) => ({ ...e, position: i })),
  ]);
}

// --- deleting -----------------------------------------------------------------------------------

export type DeleteMode = "splice" | "keep_fallback" | "whole";

export interface DeletePlan {
  /** Whether it can be done at all, and what the dialog says when it cannot. */
  allowed: boolean;
  reason?: string;
  /** The steps that go. One for a splice; a subtree for a split. */
  going: string[];
  /** Branch conditions elsewhere that read this question's answer. */
  readsIt: number;
  /** Whether the dialog has to open at all: a plain splice is covered by undo. */
  confirm: boolean;
}

/** What deleting this step would do, before anybody is asked to confirm it. */
export function planDelete(graph: Graph, key: string, mode: DeleteMode = "splice"): DeletePlan {
  const node = nodeOf(graph, key);
  if (!node) return { allowed: false, reason: "That step is not in this flow.", going: [], readsIt: 0, confirm: false };

  if (endsTheWalk(node)) {
    const others = graph.nodes.filter((n) => endsTheWalk(n) && n.key !== key);
    if (!others.length) {
      return {
        allowed: false,
        reason: "Every path has to end in an outcome, and this is the only one. Change it, or add another first.",
        going: [],
        readsIt: 0,
        confirm: true,
      };
    }
  }

  const mine = outOf(graph, key);
  const readsIt = node.field_key ? clausesReading(graph, node.field_key, key) : 0;

  if (mine.length <= 1) {
    // One way out: the chain closes over it and keeps going.
    return { allowed: true, going: [key], readsIt, confirm: readsIt > 0 };
  }

  const going = new Set<string>([key]);
  if (mode === "keep_fallback") {
    const fallback = mine.find(isFallback);
    const kept = fallback ? subtreeOf(graph, fallback.to_key, key) : new Set<string>();
    for (const branch of mine) {
      if (fallback && branch.position === fallback.position) continue;
      for (const inner of subtreeOf(graph, branch.to_key, key)) if (!kept.has(inner)) going.add(inner);
    }
  } else {
    // Where the branches come back together. `postDominator` finds the first node they all
    // reach; when they all land on the SAME node that node is itself the meeting point, and the
    // split's subtree stops short of it. Without this a card whose branches share one landing
    // took the landing — and so the rest of the flow — with it.
    const shared = new Set(mine.map((e) => e.to_key));
    const merge =
      mergeOf(graph, key) ??
      postDominator(graph, key) ??
      (shared.size === 1 ? [...shared][0]! : null);
    for (const branch of mine) {
      for (const inner of subtreeOf(graph, branch.to_key, key, merge)) going.add(inner);
    }
  }
  // Orphans are counted against the graph as it will be *after* the splice reconnects what led
  // here to where the branches rejoin. Counting before that makes the merge point and everything
  // past it look unreachable, which would delete the rest of the flow.
  // What led here is reconnected to where the branches met, so the chain closes over the card
  // rather than ending at it. The stored merge is a cache and is usually absent on a card built
  // by hand, so the same derivation is used.
  const rejoin =
    mergeOf(graph, key) ??
    postDominator(graph, key) ??
    (new Set(mine.map((e) => e.to_key)).size === 1 ? mine[0]!.to_key : null);
  const onward =
    mode === "keep_fallback" ? (mine.find(isFallback)?.to_key ?? rejoin) : rejoin;
  const spliced: Graph = {
    ...graph,
    edges: graph.edges.map((e) => (e.to_key === key && onward ? { ...e, to_key: onward } : e)),
  };
  for (const orphan of orphansWithout(spliced, going)) going.add(orphan);
  return { allowed: true, going: [...going], readsIt, confirm: true };
}

/** The steps reachable only through this branch, stopping where the branches rejoin. */
function subtreeOf(graph: Graph, from: string, split: string, merge: string | null = null): Set<string> {
  const found = new Set<string>();
  const pending = [from];
  while (pending.length) {
    const key = pending.pop()!;
    if (key === split || key === merge || found.has(key) || !nodeOf(graph, key)) continue;
    found.add(key);
    for (const edge of outOf(graph, key)) pending.push(edge.to_key);
  }
  return found;
}

function clausesReading(graph: Graph, fieldKey: string, exceptFrom: string): number {
  let count = 0;
  for (const edge of graph.edges) {
    if (edge.from_key === exceptFrom) continue;
    for (const clause of edge.conditions) {
      if (clause.source === "answer" && clause.field === fieldKey) count += 1;
    }
  }
  return count;
}

/**
 * Take a step out.
 *
 * A step with one way out splices: everything that led to it now leads where it led, keeping
 * each branch's own conditions, so the chain is unbroken. A split takes its branches with it,
 * except the Fallback's chain when that is what was asked for, and what it leaves behind
 * carries on from where the branches rejoined.
 */
export function removeNode(graph: Graph, key: string, mode: DeleteMode = "splice"): Graph {
  const plan = planDelete(graph, key, mode);
  if (!plan.allowed) return graph;
  const node = nodeOf(graph, key);
  if (!node) return graph;

  const mine = outOf(graph, key);
  const going = new Set(plan.going);
  let onward: string | null = null;
  // Where what led here is reconnected. The stored merge is a cache and is absent on a card
  // built by pressing "Add a branch", so it is derived the same way `planDelete` derives it:
  // without this the chain ended at the deleted card instead of closing over it.
  const rejoin =
    mergeOf(graph, key) ??
    postDominator(graph, key) ??
    (mine.length > 1 && new Set(mine.map((e) => e.to_key)).size === 1 ? mine[0]!.to_key : null);
  if (mine.length <= 1) {
    onward = mine[0]?.to_key ?? null;
  } else if (mode === "keep_fallback") {
    onward = mine.find(isFallback)?.to_key ?? rejoin;
  } else {
    onward = rejoin;
  }
  if (onward && going.has(onward)) onward = null;

  const nodes = graph.nodes.filter((n) => !going.has(n.key));
  const edges: FlowEdge[] = [];
  for (const edge of graph.edges) {
    if (going.has(edge.from_key)) continue;
    if (going.has(edge.to_key)) {
      if (edge.to_key !== key || !onward) continue;
      edges.push({ ...edge, to_key: onward });
      continue;
    }
    edges.push(edge);
  }
  const start = going.has(graph.start ?? "") ? onward : graph.start;
  const cleaned = nodes.map((n) =>
    going.has(String(n.config?.merge ?? "")) ? { ...n, config: { ...n.config, merge: onward } } : n,
  );
  let next: Graph = { ...graph, start, nodes: cleaned, edges };
  for (const n of cleaned) next = tidy(next, n.key);
  return next;
}

/** Take one branch of a split out, with whatever only it reached. */
export function removeBranch(graph: Graph, key: string, position: number): Graph {
  const mine = outOf(graph, key);
  const branch = mine.find((e) => e.position === position);
  // The catch-all goes like any other branch now that it is optional; what cannot go is the last
  // one of all, which would leave the step leading nowhere.
  if (!branch || mine.length < 2) return graph;

  // Only what THIS branch alone reached may go.
  //
  // `subtreeOf` stops at the merge, and the merge is a cache: a split built by pressing "Add a
  // branch" never stores one, so it was null and the walk ran to the end of the flow — removing
  // one of seven branches took the node they all landed on, and with it every other branch.
  // A post-dominator is not enough either: when some branches share an intermediate step, it
  // sits further downstream and the walk still swallows what the survivors use. So what stays is
  // asked directly — anything a surviving branch can still reach is not this branch's to take.
  const merge = mergeOf(graph, key) ?? postDominator(graph, key);
  const kept = new Set<string>();
  for (const other of mine) {
    if (other.position === position) continue;
    for (const reached of subtreeOf(graph, other.to_key, key, merge)) kept.add(reached);
  }
  const going = new Set(
    [...subtreeOf(graph, branch.to_key, key, merge)].filter((one) => !kept.has(one)),
  );
  const nodes = graph.nodes.filter((n) => !going.has(n.key));
  const edges = graph.edges.filter(
    (e) =>
      !(e.from_key === key && e.position === position) &&
      !going.has(e.from_key) &&
      !going.has(e.to_key),
  );
  return tidy({ ...graph, nodes, edges }, key);
}

/**
 * Whether this branch may be taken out.
 *
 * Any branch may, catch-all included: it is optional, so removing it is a real edit rather than a
 * way to strand a lead. The last branch of all is what stays, because a split with nothing out of
 * it leads nowhere.
 */
export function branchRemovable(graph: Graph, key: string, position: number): boolean {
  const mine = outOf(graph, key);
  return mine.length > 1 && mine.some((e) => e.position === position);
}

/**
 * Add the catch-all a step does not have: a last branch with no conditions.
 *
 * Where the branches still rejoin, it joins them there — that is the base flow carrying on.
 * Where each branch carries its own tail, it gets **an end of its own**: landing it on the last
 * branch's card looked like the new path had copied that branch, and worse, an edit meant for
 * "everyone else" then quietly changed the path it was sharing. This is `addBranchHere`'s rule,
 * for the same reason.
 */
export function addCatchAll(graph: Graph, key: string): Graph {
  const mine = outOf(graph, key);
  if (!mine.length || mine.some(isFallback)) return graph;
  const landing = mergeOf(graph, key) ?? postDominator(graph, key);
  if (landing && nodeOf(graph, landing)) {
    return addEverybodyElse(graph, key, landing, mine.length);
  }
  const made = keyFrom("qualified", graph.nodes.map((n) => n.key));
  const withEnd: Graph = {
    ...graph,
    nodes: [
      ...graph.nodes,
      { key: made, kind: "outcome", title: "Qualified", field_key: null, outcome: "continue", config: {} },
    ],
  };
  return addEverybodyElse(withEnd, key, made, mine.length);
}

function addEverybodyElse(graph: Graph, key: string, to: string, position: number): Graph {
  const edge: FlowEdge = {
    from_key: key,
    to_key: to,
    position,
    label: "Everyone else",
    conditions: [],
  };
  return { ...graph, edges: [...graph.edges, edge] };
}

/**
 * Take every path off a step, leaving it with one way on.
 *
 * The Fallback is the base flow, so what it leads to is what survives; the other branches go with
 * whatever only they reached. This is the action the Fallback's own panel describes — it used to
 * say "delete the step that made them" and leave the admin to work out which step that was.
 */
export function undoSplit(graph: Graph, key: string): Graph {
  const mine = outOf(graph, key);
  if (mine.length < 2) return graph;
  // What survives: the catch-all when there is one, since that is the base flow, and otherwise
  // the FIRST branch — the path the admin built first, and the one the step led to before it was
  // split. Keeping the last silently threw that away and left the newest branch standing.
  const kept = mine.find(isFallback) ?? mine[0]!;
  let next = graph;
  // Newest first, so a position is never re-used by the time the next one is removed.
  for (const branch of [...mine].reverse()) {
    if (branch.position === kept.position) continue;
    next = removeBranch(next, key, branch.position);
  }
  // The one branch left is the whole way on, so it carries no conditions and no name.
  const left = outOf(next, key)[0];
  if (!left) return next;
  next = updateEdge(next, key, left.position, { label: null, conditions: [] });
  // The merge it remembered only meant something while it was a split.
  return {
    ...next,
    nodes: next.nodes.map((n) => {
      if (n.key !== key) return n;
      const { merge: _gone, ...rest } = n.config ?? {};
      return { ...n, config: rest };
    }),
  };
}

// --- what the canvas draws ----------------------------------------------------------------------

export interface Branch {
  position: number;
  label: string | null;
  conditions: Clause[];
  /** What hangs under this branch, drawn the same way the trunk is: a branch may split again. */
  rows: Row[];
}

export interface Row {
  kind: "step" | "split";
  key: string;
  /** For a split: one column per branch. */
  branches?: Branch[];
  merge?: string | null;
  /**
   * The `position` of the one branch leaving a step, when it has one.
   *
   * Carried so the board can name the line it draws below this card and the live canvas can ask
   * whether that branch was walked (ACL-338). It is not always 0: removing a branch leaves the
   * survivors' positions alone, so the last branch out of a step can be any number.
   */
  out?: number;
}

/** How deep the canvas will draw nested splits. Past this a flow is unreadable anyway, and the
 *  server refuses to publish one (NODES_MAX guards the rest). */
export const DEPTH_MAX = 5;

/**
 * The canvas, top to bottom: a chain of steps, and a row of columns wherever one fans out.
 *
 * **A branch may split again**, as the reference's second level does, so a column holds rows of
 * its own rather than a flat list of steps: `chainFrom` recurses through `walk`. Without that a
 * nested split's steps are in the graph and simply never drawn, which is how an admin builds a
 * branch they cannot see.
 *
 * It stops a split's columns where its branches rejoin, so the merge point is drawn once under
 * the row rather than repeated in every column.
 */
export function layout(graph: Graph): Row[] {
  return walk(graph, graph.start, null, new Set<string>(), 0);
}

function walk(
  graph: Graph,
  from: string | null,
  stop: string | null,
  seen: Set<string>,
  depth: number,
): Row[] {
  const rows: Row[] = [];
  let key: string | null = from;
  const local = new Set<string>();
  while (key && key !== stop && !local.has(key) && nodeOf(graph, key)) {
    local.add(key);
    seen.add(key);
    const mine = outOf(graph, key);
    if (mine.length <= 1) {
      rows.push({ kind: "step", key, out: mine[0]?.position });
      key = mine[0]?.to_key ?? null;
      continue;
    }
    if (depth >= DEPTH_MAX) {
      // Draw the split itself and stop: a flow this deep is past what the canvas can show.
      rows.push({ kind: "split", key, merge: null, branches: [] });
      break;
    }
    // The stored merge is a cache that goes stale; without the derived fallback a null one makes
    // every column re-walk the tail of the flow and the steps after a split are drawn once per
    // branch. Only the drawing uses the fallback: the deletion paths keep the stored value, where
    // a wider merge would take more nodes with it than the admin asked for.
    const merge = mergeOf(graph, key) ?? postDominator(graph, key);
    rows.push({
      kind: "split",
      key,
      merge,
      branches: mine.map((edge) => ({
        position: edge.position,
        label: edge.label,
        conditions: edge.conditions,
        rows: walk(graph, edge.to_key, merge, seen, depth + 1),
      })),
    });
    key = merge;
  }
  return rows;
}
